// src/Components/CertificatePreview.jsx
import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './CertificatePreview.css';
import {
  renderDeclaration,
  renderSkillsLine,
} from '../config/certificateTemplates';

const VERIFY_BASE =
  process.env.REACT_APP_VERIFY_BASE || 'https://avtechbmt.cloud/verify';

const LOGO_PATH = '/logo.jpeg';

export default function CertificatePreview({
  data,
  mode = 'form',
  printId = 'printable-certificate',
}) {
  const paperRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!data) return null;

  const isProfessional = data.participantType === 'professional';
  const orgName = (data.universityName || '').trim();

  const fmtDate = (d) => {
    if (!d) return '__________';
    const dt = new Date(d);
    if (isNaN(dt)) return '__________';
    return dt.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const fmtShort = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt)) return '—';
    return dt.toLocaleDateString('en-IN', {
      month: 'short',
      year: 'numeric',
    });
  };

  const fmtPeriod = (d) => {
    if (!d) return 'MMM YYYY';
    const dt = new Date(d);
    if (isNaN(dt)) return 'MMM YYYY';
    return dt.toLocaleDateString('en-IN', {
      month: 'short',
      year: 'numeric',
    });
  };

  const verifyUrl = data.verificationToken
    ? `${VERIFY_BASE}/${data.verificationToken}`
    : `${VERIFY_BASE}/preview`;

  const verifyUrlDisplay = verifyUrl.replace(/^https?:\/\//, '');
  const handlePrint = () => window.print();

  const periodStart = data.startDate || data.internshipStart || data.issueDate;
  const periodEnd = data.endDate || data.internshipEnd || data.issueDate;

  /* ---------------------------------------------------------------------- */
  /* Direct PDF download via hidden iframe (Perfect Print Match)            */
  /* ---------------------------------------------------------------------- */
  const handleDownloadPdf = async () => {
    if (!paperRef.current || downloading) return;
    setDownloading(true);

    try {
      const node = paperRef.current;
      
      // 1. Create a hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      // 2. Write the certificate HTML into the iframe
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Certificate</title>
            <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" />
            <style>
              /* Inject the CSS styles */
              ${Array.from(document.styleSheets)
                .map((styleSheet) => {
                  try {
                    return Array.from(styleSheet.cssRules)
                      .map((rule) => rule.cssText)
                      .join('');
                  } catch (e) {
                    return ''; // Ignore CORS-blocked stylesheets
                  }
                })
                .join('')}
              
              /* Force print layout inside iframe */
              @page { size: A4 landscape; margin: 0; }
              body { margin: 0; padding: 0; background: #fff; overflow: hidden; }
              .cert-paper { 
                width: 297mm !important; 
                height: 210mm !important; 
                max-width: none !important;
                aspect-ratio: auto !important;
                box-shadow: none !important;
                border-radius: 0 !important;
              }
              .cert-frame-outer { inset: 6mm !important; }
              .cert-preview-actions { display: none !important; }
            </style>
          </head>
          <body>
            ${node.outerHTML}
          </body>
        </html>
      `);
      doc.close();

      // 3. Wait for images and fonts to load
      await new Promise((resolve) => {
        if (iframe.contentWindow.document.readyState === 'complete') {
          resolve();
        } else {
          iframe.onload = resolve;
        }
      });
      await new Promise((r) => setTimeout(r, 500)); // Give fonts a moment

      // 4. Trigger Print (which opens the "Save as PDF" dialog with the filename)
      const fileName = (data.recipientName || 'Certificate').trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
      iframe.contentWindow.document.title = `${fileName}.pdf`;
      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      // 5. Cleanup
      setTimeout(() => {
        document.body.removeChild(iframe);
        setDownloading(false);
      }, 1000);
      
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF.');
      setDownloading(false);
    }
  };

  return (
    <div className="cert-preview-outer">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
        @media print {
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="cert-paper" id={printId} ref={paperRef}>
        <div className="cert-frame-outer">
          <div className="cert-frame-mid">
            <div className="cert-frame-inner">
              <span className="cert-corner tl" />
              <span className="cert-corner tr" />
              <span className="cert-corner bl" />
              <span className="cert-corner br" />

              <header className="cert-header">
                <div className="cert-brand">
                  <img
                    src={LOGO_PATH}
                    alt="AV Tech Baramati Private Limited."
                    className="cert-logo-img"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="cert-brand-text">
                    <div className="cert-brand-name">
                      AV Tech Baramati Private Limited.
                    </div>
                    <div className="cert-brand-tag">
                      Empowering Skills · Building Futures
                    </div>
                  </div>
                </div>

                <div className="cert-ribbon">
                  <span>Certificate of Internship</span>
                </div>
              </header>

              <section className="cert-body-area">
                <p className="cert-preamble">This is to certify that</p>

                <div className="cert-recipient-wrap">
                  <h2 className="cert-recipient-name">
                    {data.recipientName || "Recipient's Name"}
                  </h2>
                  <span className="cert-recipient-rule" />
                </div>

                <div className="cert-declaration-block">
                  <p className="cert-declaration">
                    {renderDeclaration({
                      data,
                      orgName,
                      fmtShort,
                      isProfessional,
                    })}
                  </p>

                  <p className="cert-declaration cert-declaration-2">
                    {renderSkillsLine({ data })}
                  </p>
                </div>

                <div className="cert-period-block">
                  <div className="cert-period-label">Internship Period</div>
                  <div className="cert-period-dates">
                    <span className="cert-period-line" />
                    <span className="cert-period-value">
                      {fmtPeriod(periodStart)} &nbsp;–&nbsp; {fmtPeriod(periodEnd)}
                    </span>
                    <span className="cert-period-line" />
                  </div>
                </div>

                <div className="cert-footer-grid">
                  <div className="cert-footer-cell">
                    <div className="cert-sign-space" />
                    <div className="cert-sign-line" />
                    <div className="cert-sign-name">
                      {data.authorizedSignatory || 'Authorized Signatory'}
                    </div>
                    <div className="cert-sign-role">
                      {data.signatoryTitle || 'Director'}
                    </div>
                    <div className="cert-sign-org">
                      AVTech Baramati Private Limited
                    </div>
                  </div>

                  <div className="cert-footer-cell cert-footer-center">
                    <div className="cert-seal">
                      <div className="cert-seal-ring-outer" />
                      <div className="cert-seal-ring-inner" />
                      <div className="cert-seal-core">
                        <div className="cert-seal-star">★</div>
                        <div className="cert-seal-text">AV TECH</div>
                        <div className="cert-seal-sub">VERIFIED</div>
                      </div>
                      <div className="cert-seal-ribbon-left" />
                      <div className="cert-seal-ribbon-right" />
                    </div>
                    <div className="cert-qr-block">
                      <QRCodeSVG
                        value={verifyUrl}
                        size={120}
                        bgColor="#ffffff"
                        fgColor="#1e3a8a"
                        level="M"
                      />
                      <div className="cert-qr-caption">Scan to verify</div>
                    </div>
                  </div>

                  <div className="cert-footer-cell cert-footer-right">
                    <div className="cert-ref-block">
                      <div className="cert-ref-label">Reference No.</div>
                      <div className="cert-ref-value">
                        {data.certificateId || 'AVT-CERT-XXXX-XXXX'}
                      </div>
                    </div>

                    <div className="cert-ref-block">
                      <div className="cert-ref-label">Issued On</div>
                      <div className="cert-ref-value cert-ref-value-sm">
                        {fmtDate(data.issueDate || new Date())}
                      </div>
                    </div>

                    <div className="cert-ref-block cert-ref-block-url">
                      <div className="cert-ref-label">Verify at</div>
                      <div className="cert-ref-url-full">
                        {verifyUrlDisplay}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <div className="cert-footer-strip">
                <span>www.avtechbmt.cloud</span>
                <span className="cert-footer-dot">•</span>
                <span>avtech.baramati@gmail.com</span>
                <span className="cert-footer-dot">•</span>
                <span>Baramati, Maharashtra, India</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(mode === 'saved' || mode === 'form') && (
        <div className="cert-preview-actions">
          <button
            className="cert-btn primary"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? '⏳ Preparing PDF…' : '⬇ Download PDF'}
          </button>
          <button className="cert-btn primary" onClick={handlePrint}>
            🖨 Print / Save as PDF
          </button>
          <a
            className="cert-btn secondary"
            href={verifyUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            🔗 Open Verify Page
          </a>
        </div>
      )}
    </div>
  );
}